import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Users, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = 'https://auth.emergentagent.com';

export default function LandingPage() {
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogleAuth = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `${AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      toast.success('¡Inicio de sesión exitoso!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-200/30 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-3xl -z-10"></div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white rounded-full shadow-sm">
            <Trophy className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-slate-700">Kinosis Gestión Deportiva</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
            <span className="text-emerald-600">Kinosis</span> - Gestión Profesional de Ligas Deportivas
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Plataforma completa para administrar ligas, programar partidos y seguir resultados en tiempo real
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              data-testid="google-auth-btn"
              onClick={handleGoogleAuth}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl"
            >
              Empezar como Administrador
            </Button>
            <Button
              data-testid="login-btn"
              onClick={() => setShowLogin(true)}
              size="lg"
              variant="outline"
              className="border-2 border-slate-300 hover:border-slate-400 px-8 py-6 text-lg rounded-full"
            >
              Iniciar Sesión
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-20">
          <Card className="p-8 glass-effect card-hover border-0 shadow-lg" data-testid="feature-admin">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-slate-900">Administración Total</h3>
            <p className="text-slate-600">Crea y gestiona ligas, categorías, clubes y usuarios con permisos personalizados</p>
          </Card>

          <Card className="p-8 glass-effect card-hover border-0 shadow-lg" data-testid="feature-calendar">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-slate-900">Programación Fácil</h3>
            <p className="text-slate-600">Programa fechas, asigna canchas y partidos. Carga masiva desde Excel</p>
          </Card>

          <Card className="p-8 glass-effect card-hover border-0 shadow-lg" data-testid="feature-live">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
              <Trophy className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-slate-900">Resultados en Vivo</h3>
            <p className="text-slate-600">Sigue los partidos en tiempo real con marcadores y eventos actualizados</p>
          </Card>
        </div>
      </div>

      {/* Login Dialog */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent data-testid="login-dialog">
          <DialogHeader>
            <DialogTitle>Iniciar Sesión</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="login-email"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                data-testid="login-password"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>
              {loading ? 'Iniciando...' : 'Iniciar Sesión'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}